import { sfx } from './audio/sfx';
import { dayNumber, formatDay, formatWait, msUntilNextGrid } from './game/daily';
import { DRILLS, drillById, drillSeed } from './game/drills';
import { LATEST_SOLVER_EPOCH, solverEpochFor } from './game/epochs';
import { key } from './game/geometry';
import { fallbackPuzzle, generate, generateForDay } from './game/puzzle';
import { mwLabel } from './game/scoring';
import { GameSession, type TapResult } from './game/session';
import { score } from './game/solver';
import { classifyTopology } from './game/topology';
import type { DrillResult, Mode, Point, Progress, Puzzle, SolverEpoch } from './game/types';
import { BoardRenderer } from './render/board';
import { celebrate } from './render/confetti';
import { prefersContrast, setContrastOverride, syncContrastAttr } from './render/contrast';
import {
  prefersReducedMotion,
  setReducedMotionOverride,
  syncReducedMotionAttr,
} from './render/motion';
import {
  bumpStreak,
  emptyProgress,
  hasSeenHelp,
  hasSeenStudyWarning,
  loadContrast,
  loadDrills,
  loadHaptics,
  loadLargeText,
  loadMotionOverride,
  loadMuted,
  loadProgress,
  loadUsage,
  markHelpSeen,
  markStudyWarningSeen,
  movesFor,
  recordResult,
  recordRun,
  runFor,
  saveContrast,
  saveDrills,
  saveHaptics,
  saveLargeText,
  saveLocale,
  saveMotionOverride,
  saveMuted,
  saveProgress,
  saveUsage,
  streakIsLive,
} from './state/progress';
import {
  alreadyCounted,
  fetchField,
  loadFieldState,
  sentFor,
  submitResult,
  type FieldData,
  type FieldSubmission,
} from './state/field';
import { type FeedbackContext, feedbackEnabled } from './state/feedback';
import { flushTelemetry, sessionMs, setTelemetryEnabled, track } from './state/telemetry';
import { buildArchiveRows, renderArchive } from './ui/archive';
import type { FieldView } from './ui/field';
import { buildFieldView, renderFieldLines, yesterdayLine } from './ui/field';
import { FeedbackPanel } from './ui/feedback';
import { buildRecordsView, renderRecords } from './ui/records';
import { rankKey } from './ui/tiers';
import { must, setVisible } from './ui/dom';
import type { Locale } from './ui/locale';
import { LOCALES, LOCALE_NAME, isLocale, locale, localeTag, setLocale } from './ui/locale';
import { applyStaticStrings, str } from './ui/strings';
import { BUZZ, buzz, hapticsOn, hapticsSupported, setHaptics } from './ui/haptics';
import { hud } from './ui/hud';
import { Overlay, closeOnEscape } from './ui/overlays';
import { ResultPanel, buildResultView } from './ui/result';
import { applyFlag, bindSwitch, flagApplied } from './ui/settings';
import { buildShareText, canShareSheet, shareResult } from './ui/share';
import { quietToast, restateToast, toast } from './ui/toast';
import { buildYardRows, renderYard } from './ui/yard';

/** Pause after the surge lands before the result panel slides in. */
const PANEL_DELAY = 420;

/** Hold on the finished demo grid before the rules cover it (first run only). */
const INTRO_HOLD = 900;

/**
 * How long the page must sit untouched before an update may reload it while
 * visible. Long enough to clear the solve fanfare and an idle glance, short
 * enough that "as soon as possible" still means this session. A hidden page
 * skips the wait — nobody is watching a reload nobody can see.
 */
const UPDATE_LULL_MS = 60_000;

/** The nav bar's views (STYLEGUIDE §7). The board screen is 'today'. */
type AppView = 'today' | 'archive' | 'drills' | 'profile';

const VIEW_IDS: Record<AppView, string> = {
  today: 'viewToday',
  archive: 'viewArchive',
  drills: 'viewDrills',
  profile: 'viewProfile',
};

const TAB_IDS: Record<AppView, string> = {
  today: 'navToday',
  archive: 'navArchive',
  drills: 'navDrills',
  profile: 'navProfile',
};

/** The same cells, in any order. */
function sameLayout(a: Point[], b: Point[]): boolean {
  if (a.length !== b.length) return false;
  const cells = new Set(a.map(key));
  return b.every((t) => cells.has(key(t)));
}

/**
 * Tapping the plant or a house is silent — there's nothing to explain.
 * Nudges state the physics and the fix, the right way round: wires cross
 * water and clear peaks; terrain stops towers.
 *
 * Built per nudge rather than held in a const: a table read once at import
 * would still be speaking the language the app booted in.
 */
function rejectMessage(reason: 'water' | 'mtn' | 'out-of-towers'): string {
  return { water: str().denyWater, mtn: str().denyMtn, 'out-of-towers': str().denyLimit }[reason];
}

/**
 * Wires the game to the page: owns the current session, the board, and the
 * player's saved progress, and keeps the three in agreement.
 */
export class WirespanApp {
  private readonly board: BoardRenderer;
  private readonly panel = new ResultPanel();
  /** Today's grid number. Not readonly: it moves when midnight passes mid-session. */
  private today = dayNumber();

  private studyWarned = false;

  /**
   * Whether the player is sending usage data. Defaults on, and the switch in
   * settings turns off the stream and the field readout together (GDD §12).
   */
  private usage = true;

  /**
   * The grid currently being measured, or `null` between grids.
   *
   * A grid never really "ends" — the whole point is that tuning continues — so
   * what is measured is the visit: it opens when a grid is dealt and closes
   * when the player leaves for another one or leaves the page. `built` is
   * carried because a grid nobody touched and a grid somebody fought and lost
   * are different failures.
   */
  private measuring: {
    mode: 'daily' | 'archive' | 'practice' | 'drill';
    grid?: number;
    drill?: string;
    at: number;
    built: boolean;
    /**
     * The window has already been reported. Set when the page hides and
     * cleared when it comes back, so a grid the player returns to reports
     * again with a longer stretch and a better best rather than falling
     * silent. Readers take the last report per session and grid.
     */
    reported: boolean;
  } | null = null;

  /** When the player last touched the page. Zero until they have. */
  private lastInputAt = 0;

  /** The day's counters for the grid on the board, once they have landed. */
  private field: FieldData | null = null;

  /** Yesterday's standing, once it has landed. Painted with the card. */
  private yesterdayView: FieldView | null = null;

  private helpOverlay!: Overlay;
  private studyOverlay!: Overlay;
  private feedbackOverlay!: Overlay;
  private feedback!: FeedbackPanel;
  private settingsOverlay!: Overlay;
  private langOverlay!: Overlay;

  /** Which view the nav bar is showing. The board screen is 'today'. */
  private activeView: AppView = 'today';
  /** A programmatic history.back() is in flight; its popstate is ours. */
  private suppressPop = false;

  private progress: Progress = emptyProgress();
  private drills: Record<string, DrillResult> = {};
  private session!: GameSession;
  private mode: Mode = 'daily';
  private playDay = this.today;
  /** Which drill is on the board, or `null` outside the training yard. */
  private drillId: string | null = null;

  constructor(boardSvg: SVGSVGElement) {
    this.board = new BoardRenderer(boardSvg);
  }

  /** Load progress, wire the chrome, and deal today's grid. */
  async start(): Promise<void> {
    await this.loadSettings();
    this.bindControls();
    this.progress = await loadProgress();
    this.drills = await loadDrills();
    await loadFieldState();
    this.studyWarned = await hasSeenStudyWarning();
    this.showStreak();
    const seen = await hasSeenHelp();

    if (seen) {
      this.startDaily(this.today);
    } else {
      // Burned at demo start: a reload mid-intro boots normally, and the
      // rules stay one tap away on the ? button.
      void markHelpSeen();
      this.playIntro();
    }
    this.loadYesterday();

    // Keep the card's next-grid line honest while the app sits open.
    setInterval(() => this.tickNextGrid(), 30_000);
  }

  /**
   * First run only: a completed grid surges to life before the rules open,
   * so the player has seen a working grid before reading a word (GDD §7).
   * A canned grid, never today's deal — the engineers' grid stays unrevealed.
   */
  private playIntro(): void {
    const demo = fallbackPuzzle();
    this.board.paintHourTint(new Date().getHours());
    // No sound callbacks: pre-gesture, no audio context may exist.
    this.board.mount(demo, { onCell: () => {} });
    const { maxDelay } = this.board.render(demo.seedTowers);
    this.board.setPlantOutput(100);
    // Raw setTimeout, not board.schedule(): under reduced motion surge timers
    // fire synchronously, and any re-render would cancel scheduled work.
    setTimeout(() => {
      this.helpOverlay.open();
      // Deal today behind the backdrop — unless the player already dealt a
      // grid themselves mid-demo (Practice/Archive set `measuring`).
      if (!this.measuring) this.startDaily(this.today);
    }, maxDelay + INTRO_HOLD);
  }

  /**
   * Whether an app update may apply right now — applying is a reload, so the
   * bar is that the reload reproduces exactly what the player has (GDD §8).
   *
   * Today's daily re-deals itself and restores the saved best, so it is safe
   * whenever the board is showing that saved state. Every other board —
   * practice, drills, the archive, a daily the clock has rolled past — reboots
   * into today, so only an untouched visit has nothing to lose. Never
   * mid-round: an open overlay, a revealed reference, or recent input all mean
   * someone is in the middle of something.
   */
  safeToReload(): boolean {
    // Still booting — nothing on screen to preserve.
    if (!this.session) return true;
    const overlays = [this.helpOverlay, this.studyOverlay, this.settingsOverlay, this.langOverlay];
    if (overlays.some((o) => o.isOpen)) return false;
    // Browsing a view is mid-something, exactly like an open overlay.
    if (this.activeView !== 'today') return false;
    if (this.board.showingReference) return false;
    // An untouched launch has no round to protect: whatever grid restored
    // under it restores again after the reload. This is GDD §8's "at latest
    // on next launch" — without it, an update could wait out a whole session
    // behind the gates below and then some (an iOS page resumed from the
    // switcher for days never launches at all).
    if (this.lastInputAt === 0) return true;
    if (!document.hidden && Date.now() - this.lastInputAt < UPDATE_LULL_MS) return false;
    // dayNumber() directly, not this.today: the rollover tick runs every 30s,
    // and a reload in that gap would deal a grid the player wasn't shown.
    if (this.mode === 'daily' && this.playDay === dayNumber()) {
      return sameLayout(this.session.placed, this.session.best?.towers ?? []);
    }
    return !this.measuring?.built;
  }

  // ---------- grids ----------

  private startDaily(day: number = this.today): void {
    this.mode = 'daily';
    this.playDay = day;
    this.drillId = null;
    // Both epochs are resolved from the grid number, never from the current
    // version of the code: grid #12 deals the map and races the target it has
    // always had (GDD §3.5, §5).
    this.beginSession(generateForDay(day), day, day, solverEpochFor(day));

    // How it was played is restored before what was achieved: `restore()`
    // latches the perfect grid, and flawless has to be on the session by then.
    this.session.restoreRun(runFor(this.progress, day));
    const saved = this.progress.results[day];
    if (saved) {
      this.session.restore(saved.towers, saved.firstMW ?? saved.mw ?? null, saved.mwTrail);
    }

    this.field = null;
    this.loadField(day);
    this.mountAndDraw();
    this.paintDaytag();
  }

  private startPractice(): void {
    this.mode = 'practice';
    this.drillId = null;
    const seed = 100000 + Math.floor(Math.random() * 899999);
    this.beginSession(generate(seed), seed, null, LATEST_SOLVER_EPOCH);
    this.mountAndDraw();
    this.paintDaytag();
  }

  /**
   * A drill from the training yard (GDD §4.6): authored, not dealt. No
   * streak, no share, no day number — a lesson, not a result.
   */
  private startDrill(id: string): void {
    const drill = drillById(id);
    if (!drill) return;
    this.mode = 'drill';
    this.drillId = id;
    this.beginSession(drill.puzzle, drillSeed(id), null, LATEST_SOLVER_EPOCH);
    // A strategy drill opens on its naive line, already lit (GDD §4.6): the
    // layout it exists to beat is the thing to see, and the mount plays the
    // surge into it the way a restored finished daily reopens. The first-light
    // primer opens empty — its lesson is the tap itself, and a player who
    // found the yard before the daily has never placed a tower.
    if (drill.family !== 'first-light') this.session.prebuild(drill.puzzle.seedTowers);
    this.mountAndDraw();
    this.paintDaytag();
  }

  /**
   * The tag beside the wordmark: which grid this is, in the language being
   * spoken. One painter for all three modes, because a language change has to
   * be able to rewrite whatever is currently up there.
   */
  private paintDaytag(): void {
    const tag = must('daytag');
    if (this.mode === 'practice') {
      tag.innerHTML = str().daytagPractice;
      return;
    }
    if (this.mode === 'drill') {
      // The tag says only that a drill is up: the lesson line under the
      // board already leads with the drill's name, and the masthead has no
      // room to say it twice (STYLEGUIDE §7).
      tag.innerHTML = str().daytagDrill;
      return;
    }
    tag.innerHTML = str().daytagDaily(this.playDay, formatDay(this.playDay, localeTag()));
  }

  private beginSession(puzzle: Puzzle, seed: number, day: number | null, epoch: SolverEpoch): void {
    // Before the session is replaced, not after. `closeMeasure` reads the
    // session for the result it reports, so closing from `openMeasure` — which
    // runs later, in mountAndDraw — read the *incoming* grid's board and filed
    // it under the outgoing grid's number. Every grid left by starting another
    // one reported as a walk-out, however well it had gone.
    this.closeMeasure();
    this.panel.hide();
    this.board.clearReference();
    hud.setMegawatts(null);
    this.session = new GameSession(score(puzzle, seed, epoch), this.mode, day);
  }

  /** Archive days are dailies replayed, and the two say different things. */
  private trackMode(): 'daily' | 'archive' | 'practice' | 'drill' {
    if (this.mode !== 'daily') return this.mode;
    return this.playDay === this.today ? 'daily' : 'archive';
  }

  /** What every event about this grid is filed under. */
  private gridKeys(): Record<string, string | number | undefined> {
    const m = this.measuring;
    return m ? { mode: m.mode, grid: m.grid, drill: m.drill } : {};
  }

  /**
   * What travels with a feedback message, and the whole of it (GDD §13).
   *
   * The same three facts the panel prints above the send button, so the list
   * there and the payload here cannot come apart. Practice and drills have no
   * grid number, and the line says so by naming only the mode and the build.
   */
  private feedbackContext(): FeedbackContext {
    return {
      ...(this.mode === 'daily' ? { grid: this.playDay } : {}),
      mode: this.trackMode(),
      build: __BUILD_ID__,
    };
  }

  /** Close the previous grid's window and open one for the grid just dealt. */
  private openMeasure(): void {
    this.measuring = {
      mode: this.trackMode(),
      ...(this.mode === 'daily' ? { grid: this.playDay } : {}),
      ...(this.drillId ? { drill: this.drillId } : {}),
      at: sessionMs(),
      built: false,
      reported: false,
    };
    track('grid_start', { ...this.gridKeys(), archetype: this.session.puzzle.archetype });
  }

  /**
   * Report how the grid went, once.
   *
   * A grid that was never lit reports that it was left, and how far it got. A
   * grid that was lit reports its result — which is the reading the player
   * already has, never a measurement taken behind them (GDD §12).
   */
  private closeMeasure(opts: { keep?: boolean } = {}): void {
    const m = this.measuring;
    if (!m || m.reported) return;
    // Hiding the page reports the window but keeps it: the player may well be
    // back on the same grid in a minute, and dropping it would leave every
    // event after the first app switch with nothing to file itself under.
    if (opts.keep) m.reported = true;
    else this.measuring = null;
    const keys = { mode: m.mode, grid: m.grid, drill: m.drill };
    const gridMs = Math.max(0, sessionMs() - m.at);

    const best = this.session.best;
    const tier = this.session.tier();
    if (!best || !tier) {
      track('grid_quit', { ...keys, phase: m.built ? 'building' : 'untouched' });
      return;
    }

    // The submission goes at the end of the visit, not at the first solve:
    // the best is final by then, and a field built from cold reads would
    // describe a race nobody actually ran. A visit that ends better than the
    // last one amends what was sent rather than sending again — `submitResult`
    // owns that rule, so there is nothing to check here.
    if (this.usage && m.mode === 'daily' && m.grid === this.today) {
      submitResult({
        grid: this.today,
        mw: best.mw,
        cold: this.session.firstMW,
        tier: tier.rank,
        perfect: this.session.matchedReference,
        flawless: this.session.flawless,
        beatRef: this.session.beatReference,
      });
    }

    track('grid_end', {
      ...keys,
      // Carried here as well as on grid_solve, because a grid solved in an
      // earlier session is restored rather than re-solved: grid_solve never
      // fires for it, and without this its first take would be missing from
      // every reading of the cold-read distribution.
      coldMW: this.session.firstMW ?? undefined,
      bestMW: best.mw,
      bestTowers: best.towers.length,
      tier: tier.rank,
      bestCount: this.session.mwTrail.length,
      perfect: this.session.matchedReference,
      flawless: this.session.flawless,
      beatRef: this.session.beatReference,
      studied: Boolean(this.session.runRecord.studied),
      underPar: this.session.underPar,
      streak: this.activeStreak() ?? undefined,
      gridMs,
    });
    // The transport's own lifecycle listener runs BEFORE this one — it was
    // armed in main.ts, before app.start bound anything — so on a hidden or
    // pagehide event it posts what is buffered and never sees the grid_end
    // this call just added. Flush explicitly so the last grid of a session
    // reaches the wire instead of dying with the tab.
    flushTelemetry('manual');
  }

  private mountAndDraw(): void {
    this.openMeasure();
    this.board.paintHourTint(new Date().getHours());
    this.board.mount(this.session.puzzle, {
      onCell: (p) => this.onCell(p),
      onEnergized: (order) => sfx.play('energize', order),
      onHouseLit: (order) => sfx.play('houseLit', order),
    });
    this.board.onHoverEnter = (p) => {
      if (this.session.hasTowerAt(p) || this.session.placed.length >= this.session.puzzle.par) {
        return;
      }
      this.board.showGhost(p);
    };
    this.setButtons();
    // Every deal passes through here, so this is where the lit tab follows
    // the board's provenance and the chip obeys the mode: a live streak
    // never shows in practice, in the training yard, or on an archive
    // replay (STYLEGUIDE §7).
    this.paintTabs();
    this.showStreak();
    this.refresh();
  }

  /**
   * The one contextual board action (STYLEGUIDE §7): re-dealing a practice
   * map. How-to-play lives in the masthead and practice in the Drills view,
   * so the row under the board is empty everywhere else.
   */
  private setButtons(): void {
    setVisible(must('newMapBtn'), this.mode === 'practice');
    // A drill is a lesson, not a result: there is nothing to brag about.
    setVisible(must('shareBtn'), this.mode !== 'drill');
    this.paintDrillLesson();
    this.syncReveal();
  }

  /**
   * The drill's lesson, kept in view for the whole attempt (GDD §4.6): the
   * `teaches` line, with the naive line's reading as the target. The goal
   * sentence retires once the line is beaten; the lesson stays.
   */
  private paintDrillLesson(): void {
    const el = must('drillLesson');
    const drill = this.mode === 'drill' && this.drillId ? drillById(this.drillId) : null;
    if (!drill) {
      el.hidden = true;
      return;
    }
    // The target reading belongs to a board that opened on a naive line; the
    // primer's lesson is complete in itself.
    const naive = this.session.naiveLineMW;
    const goal = naive != null && !this.session.solvedOnce ? str().drillGoal(mwLabel(naive)) : '';
    // The name leads in bold, so the line reads as this drill's lesson and
    // not ambient chrome: "Avoid triangles: A wire appears between..."
    const copy = str().drills[drill.id];
    el.innerHTML = str().drillLesson(copy.name, copy.teaches, goal);
    el.hidden = false;
  }

  // ---------- play ----------

  private onCell(p: Point): void {
    const result: TapResult = this.session.tap(p);
    this.board.clearGhost();

    if (result.type === 'rejected') {
      if (result.reason === 'occupied') return;
      if (result.reason === 'out-of-towers') {
        // The reason lives in the HUD, so the flash answers there.
        hud.flashTowerLimit();
        sfx.play('limit');
        buzz(BUZZ.limit);
      } else {
        // The reason is the cell itself, so the pulse answers there.
        sfx.play('deny');
        this.board.pulseDenied(p);
        buzz(BUZZ.deny);
      }
      // Held in a const so the narrowing above survives into the closure:
      // 'occupied' returned before this line, and a thunk cannot be re-checked.
      const reason = result.reason;
      toast(() => rejectMessage(reason));
      return;
    }

    if (result.type === 'built') {
      if (this.measuring) this.measuring.built = true;
      sfx.play('build');
      buzz(BUZZ.build);
      this.refresh({ built: result.at });
      this.board.pulseRange(result.at);
    } else {
      sfx.play('remove');
      buzz(BUZZ.remove);
      // The ever-removed flag is written the moment it changes, not on
      // completion, so flawless cannot be laundered by a reload (GDD §3.6).
      this.persistRun();
      this.refresh({ removed: result.at });
    }
  }

  private refresh(opts: { built?: Point; removed?: Point } = {}): void {
    const { net, maxDelay } = this.board.render(this.session.placed, {
      justBuilt: opts.built,
      justRemoved: opts.removed,
    });
    const ev = this.session.evaluate();
    const puzzle = this.session.puzzle;

    hud.setTowers(this.session.placed.length, puzzle.par);
    hud.setHouses(net.houseLinks.filter((h) => h.lit).length, puzzle.houses.length);
    hud.setMegawatts(ev.mw);
    // Plant stacks smoke in proportion to the MW the grid is currently
    // delivering — a dark grid holds still, a well-tuned grid churns.
    this.board.setPlantOutput(ev.mw);

    if (ev.justSolved && this.measuring) {
      track('grid_solve', {
        ...this.gridKeys(),
        coldMW: this.session.firstMW ?? undefined,
        coldTowers: this.session.placed.length,
        solveMs: Math.max(0, sessionMs() - this.measuring.at),
      });
    }
    if (ev.justPerfect) this.persistRun();
    // Beating the line retires the lesson caption's goal sentence.
    if (ev.justSolved && this.mode === 'drill') this.paintDrillLesson();
    if (ev.improved) {
      this.persistBest();
      if (!ev.justSolved) {
        // The reading is captured here, not read again on restate: the
        // player can keep tuning while the message is still up.
        const reached = mwLabel(this.session.best?.mw ?? 0);
        toast(() => str().newBest(reached));
        if (!ev.justPerfect) {
          sfx.play('newBest');
          hud.flashNewBest();
        }
      }
    }

    // The card is worth staging twice: the first time the grid lights up, and
    // the moment it becomes perfect. Everything else is quiet re-tuning.
    const stage = ev.justSolved || ev.justPerfect;
    if (ev.justSolved) {
      this.board.schedule(() => {
        this.showPanel(ev.mw, { stage: true, perfect: ev.justPerfect });
        this.panel.scrollIntoView(!prefersReducedMotion());
      }, maxDelay + PANEL_DELAY);
    } else if (this.session.solvedOnce) {
      this.showPanel(ev.mw, { stage, perfect: ev.justPerfect });
    }

    this.syncReveal();
    this.tickNextGrid();
  }

  /**
   * The card's quiet footer: a countdown to the next deal on today's grid,
   * flipping to "New grid ready." at local midnight. The board itself never
   * swaps out from under the player (GDD §6.2).
   */
  private tickNextGrid(): void {
    // A throttled background tab can stretch the first-run intro past this
    // tick's first firing, before any session exists.
    if (!this.session) return;
    const real = dayNumber();
    if (real !== this.today) {
      // Midnight passed with the app open: surface the way to the new grid
      // and let the streak chip re-evaluate, but leave the board alone. One
      // quiet toast — no cue: nothing electrical happened, and unprompted
      // audio has no place in this mix.
      this.today = real;
      this.setButtons();
      this.showStreak();
      // The card's room block reads off `today`: the grid on the board is no
      // longer the young one, and yesterday's line is now the day before it.
      if (this.session.solvedOnce) this.showPanel(this.session.evaluate().mw);
      quietToast(() => str().newGridReady);
    }
    // Refresh the sky-band tint against the operator's clock (STYLEGUIDE §1).
    // This tick runs every 30s, so a dawn or dusk shift falls in view within
    // half a minute — invisible on a fresh mount, quietly true after that.
    this.board.paintHourTint(new Date().getHours());

    const line = must('winNext');
    const relevant = this.mode === 'daily' && this.session.solvedOnce;
    if (relevant && this.playDay === this.today) {
      line.innerHTML = str().nextGridIn(
        formatWait(msUntilNextGrid(), { hours: str().waitHours, minutes: str().waitMinutes }),
      );
      line.hidden = false;
    } else if (relevant && this.playDay === this.today - 1) {
      // Exactly the rolled-over grid — an archive replay doesn't need telling.
      line.textContent = str().newGridReady;
      line.hidden = false;
    } else {
      line.hidden = true;
    }
  }

  /**
   * The field readout for the grid on the board, or `null`.
   *
   * Only the daily has a race to report, and only a solved one has a result to
   * place. A player who sends nothing sees nothing here, because the counters
   * they are reading are the ones they declined to join (GDD §4.7).
   *
   * Yesterday's settled line rides this block rather than the card's footer:
   * it is a fact about the room, so it reads with the room's other numbers,
   * and it stands on its own on a day whose own counters have not answered.
   */
  private fieldLines(): string[] | null {
    const yesterday = this.yesterdayNote();
    const alone = yesterday ? [yesterday] : null;
    const best = this.session.best;
    const tier = this.session.tier();
    if (!this.usage || this.mode !== 'daily' || !best || !tier) return alone;
    const view = buildFieldView(
      this.field?.grid === this.playDay ? this.field : null,
      { mw: best.mw, tier: tier.rank },
      { young: this.playDay === this.today, ...this.myPlace(this.playDay) },
    );
    return view ? renderFieldLines(view, yesterday) : alone;
  }

  /**
   * Whether this device's own result belongs in the counters it is reading,
   * and what those counters already hold for it.
   *
   * Counted with a record of what by: that entry comes out and the reading on
   * the card goes in. Counted by a build that kept no record: it stands as it
   * was sent, because replacing it would mean guessing what to remove. Not
   * counted at all: only today's grid is on its way to the counters — an
   * archive replay is never submitted, so it is never one of the players.
   */
  private myPlace(day: number): { mine: boolean; sent: FieldSubmission | null } {
    const sent = sentFor(day);
    return { mine: alreadyCounted(day) ? sent !== null : day === this.today, sent };
  }

  /**
   * Fetch the day's counters and redraw the card once they land.
   *
   * Nothing waits on this: the card is already correct without it, and the
   * readout simply appears if the answer arrives.
   */
  private loadField(day: number): void {
    if (!this.usage || this.field?.grid === day) return;
    void fetchField(day).then((data) => {
      if (!data || this.mode !== 'daily' || this.playDay !== day) return;
      this.field = data;
      if (this.session.solvedOnce) this.showPanel(this.session.evaluate().mw);
    });
  }

  /**
   * Yesterday's standing, on today's card.
   *
   * Only for a player who finished yesterday: a line about a race they did not
   * run places nothing.
   */
  private loadYesterday(): void {
    const day = this.today - 1;
    if (!this.usage || day < 1 || !this.progress.results[day]) return;
    const mine = this.progress.results[day];
    void fetchField(day).then((data) => {
      const view = buildFieldView(
        data,
        { mw: mine.mw, tier: rankKey(mine.tier) ?? '' },
        // Yesterday is over, so nothing about it is still coming in.
        { young: false, ...this.myPlace(day) },
      );
      this.yesterdayView = view;
      if (this.session.solvedOnce) this.showPanel(this.session.evaluate().mw);
    });
  }

  /**
   * Yesterday's line, on today's card only. It is the second daily ritual's
   * doorway (GDD §4.3), and on an archive replay it would be a fact about a
   * different day than the one on the board.
   *
   * Rendered per card rather than cached as a sentence: the language can
   * change under a line that is already on screen.
   */
  private yesterdayNote(): string | null {
    if (this.mode !== 'daily' || this.playDay !== this.today) return null;
    return this.yesterdayView ? yesterdayLine(this.yesterdayView) : null;
  }

  private showPanel(nowMW: number | null, opts: { stage?: boolean; perfect?: boolean } = {}): void {
    // A count an older version of the game took, on a grid that has one. The
    // counter is retired; the reading it left is still a fact (GDD §4.3).
    const retired = this.mode === 'daily' ? movesFor(this.progress, this.playDay) : null;
    const view = buildResultView(this.session, {
      nowMW,
      retiredMoves: retired?.frozen ? retired.count : null,
      topology: this.topologyName(),
      // The lesson retires itself: once a player has perfected any grid, they
      // have the frame these lines exist to give them (GDD §6.2).
      explain: !this.everPerfected(),
      // The same gate the countdown and the yesterday line read: only today's
      // daily still has a race whose reveal is worth promising (GDD §4.3).
      liveDaily: this.mode === 'daily' && this.playDay === this.today,
      field: this.fieldLines(),
    });

    this.panel.show(view, {
      stage: opts.stage,
      onBeat: (index, total) => {
        sfx.play('beat', index);
        if (index < total - 1) return;
        // The last chip lands, then the payoff. Beating the reference is the
        // perfect moment one register brighter, never a second celebration.
        if (opts.perfect) {
          const outdid = this.session.beatReference;
          sfx.play(outdid ? 'engineers' : 'perfect');
          celebrate(this.celebrationOrigin(), { hot: outdid });
          // Windows flare in lockstep with the fanfare — an intensification
          // of the existing house-lit beat, one clock (STYLEGUIDE §8).
          this.board.flarePerfectWindows();
          buzz(BUZZ.perfect);
        } else {
          sfx.play('win');
          buzz(BUZZ.win);
        }
      },
    });
  }

  // ---------- the engineers' drawing ----------

  /**
   * The reference is available on a grid whose race is over and which this
   * player finished (GDD §4.5). Today's grid never reveals, at any score, and
   * a grid they have not lit keeps its answer. A drill has no race, so
   * finishing it is the whole gate.
   */
  private canReveal(): boolean {
    // A drill has no race, so finishing it is the whole gate. The stored
    // result is not consulted: the button lives on the result card, which
    // exists only once this session has beaten the naive line.
    if (this.mode === 'drill') return this.session.solvedOnce;
    // Practice is nobody's race, so finishing it is the whole gate (GDD §4.3).
    if (this.mode === 'practice') return this.session.solvedOnce;
    if (this.mode !== 'daily') return false;
    return this.playDay < this.today && this.session.solvedOnce;
  }

  private syncReveal(): void {
    const btn = must('revealBtn');
    const can = this.canReveal();
    setVisible(btn, can);
    if (!can) this.board.clearReference();
    btn.textContent = this.board.showingReference ? str().revealHide : str().revealShow;

    // The second daily ritual: a way through to a finished yesterday, offered
    // only where it exists (GDD §4.3). It can never share the row with the
    // reveal, which is hidden on today's grid by the same rule.
    const onToday = this.mode === 'daily' && this.playDay === this.today;
    setVisible(must('yesterdayBtn'), onToday && Boolean(this.progress.results[this.today - 1]));

    // A beaten drill offers the next in the authored order — the yard plays
    // as a course (GDD §4.6). The last drill offers today's grid instead:
    // the course ends on the daily, not a dead card, and a course that
    // loops is a treadmill. Same slot, same tier as the card's other road
    // buttons ("Yesterday's grid").
    const idx =
      this.mode === 'drill' && this.drillId ? DRILLS.findIndex((d) => d.id === this.drillId) : -1;
    const nextBtn = must('nextDrillBtn');
    nextBtn.textContent = idx === DRILLS.length - 1 ? str().todaysGrid : str().nextDrill;
    setVisible(nextBtn, idx >= 0 && this.session.solvedOnce);
  }

  private toggleReveal(): void {
    if (!this.canReveal()) return;
    if (this.board.showingReference) {
      this.board.clearReference();
      this.syncReveal();
      return;
    }
    // Looking before a perfect marks the day. The consequence is stated the
    // first time it can apply, so the mark is never a surprise (GDD §4.3).
    // Only a daily earns the mark (persistRun no-ops elsewhere): a drill or
    // practice grid has no day to mark, so there is no consequence to
    // confirm and the warning would claim one that never happens.
    if (this.mode === 'daily' && !this.session.matchedReference && !this.studyWarned) {
      this.studyOverlay.open();
      return;
    }
    this.showReference();
  }

  private showReference(): void {
    const marks = !this.session.matchedReference;
    track('study', { grid: this.gridKeys().grid, beforePerfect: marks });
    this.session.markStudied();
    if (marks) this.persistRun();
    this.board.showReference(this.session.reference.towers);
    this.syncReveal();
  }

  /**
   * The reference's shape, named (GDD §3.5). Computed from the reference
   * layout, so it is the same name for everyone on the same day.
   */
  private topologyName(): string {
    return str().topologies[classifyTopology(this.session.puzzle, this.session.reference.towers)];
  }

  /** Has this player ever reached a perfect grid on any day? */
  private everPerfected(): boolean {
    return Object.values(this.progress.results).some((r) => r.tier === '🏆');
  }

  /** Burst from the middle of the board, not the middle of the screen. */
  private celebrationOrigin(): { x: number; y: number } | undefined {
    const box = must('board').getBoundingClientRect();
    if (!box.width) return undefined;
    return { x: box.left + box.width / 2, y: box.top + box.height * 0.45 };
  }

  /** The streak, but only when it's this player's live run on today's grid. */
  private activeStreak(): number | null {
    return this.mode === 'daily' && this.playDay === this.today ? this.progress.streak : null;
  }

  /**
   * Save how this grid is being played: the ever-removed flag, flawless, and
   * the study mark. Cheap, idempotent, and called on every change to any of
   * them rather than at the end of the session.
   */
  private persistRun(): void {
    if (this.mode !== 'daily') return;
    this.progress = recordRun(this.progress, this.playDay, this.session.runRecord);
    void saveProgress(this.progress);
  }

  private persistBest(): void {
    const best = this.session.best;
    const tier = this.session.tier();
    if (!best || !tier) return;

    if (this.mode === 'drill') {
      if (!this.drillId) return;
      // A drill keeps no layout, so a replay reopens on the naive line and
      // beating it again looks like an improvement. Compare against what is
      // stored, or a worse replay would quietly demote a finished drill.
      const kept = this.drills[this.drillId];
      if (kept && kept.wire <= best.wire) return;
      this.drills = {
        ...this.drills,
        [this.drillId]: { mw: best.mw, wire: best.wire, tier: tier.emoji },
      };
      void saveDrills(this.drills);
      return;
    }
    if (this.mode !== 'daily') return;

    if (this.playDay === this.today) this.progress = bumpStreak(this.progress, this.today);
    this.progress = recordResult(this.progress, this.playDay, {
      towers: best.towers,
      used: best.towers.length,
      mw: best.mw,
      wire: best.wire,
      firstMW: this.session.firstMW,
      tier: tier.emoji,
      mwTrail: this.session.mwTrail,
      // Stored with the result so a tier read back from storage is the tier
      // that was earned, whatever a later epoch's search would say.
      refWire: this.session.puzzle.ref.wire,
    });
    void saveProgress(this.progress);
    this.showStreak();
  }

  /**
   * The streak is today's daily business: it never shows in practice, in the
   * training yard, or on an archive replay (STYLEGUIDE §7).
   */
  private showStreak(): void {
    // The streak cell is a fixture of the readout in every mode, zero
    // included (STYLEGUIDE §7): the streak is a fact about the player, not
    // about the board on screen, so it rides through practice, drills and
    // archive replays unchanged.
    hud.setStreak(streakIsLive(this.progress, this.today) ? this.progress.streak : 0);
  }

  // ---------- views and the nav bar (STYLEGUIDE §7) ----------

  /**
   * Switch the app column to one view. The board screen is a single instance
   * the Today tab owns; list views paint fresh on entry, the way the modals
   * they replaced did. `focusHeading` moves reading focus on a real switch
   * and stays put when a repaint happens in place.
   */
  private showView(view: AppView, focusHeading = false): void {
    for (const [v, id] of Object.entries(VIEW_IDS)) {
      must(id).hidden = v !== view;
    }
    this.activeView = view;
    if (view === 'archive') this.paintArchive();
    if (view === 'drills') this.paintYard();
    if (view === 'profile') this.paintProfile();
    this.paintTabs();
    if (focusHeading && view !== 'today') {
      must(VIEW_IDS[view]).querySelector('h2')?.focus();
    }
  }

  /**
   * The lit tab is provenance: a list view lights its own tab, and the board
   * screen lights the tab it was dealt from — an archive replay keeps
   * Archive lit the way a detail screen keeps its originating tab, and a
   * practice board keeps Drills, which is the only door it has. Tapping
   * that lit tab then pops back to its list.
   */
  private paintTabs(): void {
    const byMode = {
      daily: 'today',
      practice: 'drills',
      archive: 'archive',
      drill: 'drills',
    } as const;
    const active: AppView =
      this.activeView === 'today' ? byMode[this.trackMode()] : this.activeView;
    for (const [v, id] of Object.entries(TAB_IDS)) {
      if (v === active) must(id).setAttribute('aria-current', 'page');
      else must(id).removeAttribute('aria-current');
    }
  }

  /**
   * Open a list view from the bar, holding one history entry under it so the
   * platform back gesture returns toward the board instead of leaving the
   * app. Depth never exceeds one: a second section replaces the entry.
   */
  private openSection(view: Exclude<AppView, 'today'>): void {
    track('panel_view', { panel: view });
    const held = (history.state as { view?: string } | null)?.view;
    if (held && held !== 'today') history.replaceState({ view }, '');
    else history.pushState({ view }, '');
    this.showView(view, true);
  }

  /** Return to the board screen, retiring the view's history entry. */
  private toBoard(): void {
    this.showView('today');
    const held = (history.state as { view?: string } | null)?.view;
    if (held && held !== 'today') {
      this.suppressPop = true;
      history.back();
    }
  }

  /** Profile: the records block, and whether the drop box row shows. */
  private paintProfile(): void {
    renderRecords(buildRecordsView(this.progress, this.drills, this.today));
    setVisible(must('profileFeedback'), feedbackEnabled());
  }

  // ---------- chrome ----------

  private bindControls(): void {
    // Any touch of the page counts as mid-thought, wherever it lands — the
    // update lull (safeToReload) measures from the last of these.
    const stamp = (): void => {
      this.lastInputAt = Date.now();
    };
    document.addEventListener('pointerdown', stamp);
    document.addEventListener('keydown', stamp);

    // Backdrop and Escape closes have no button to tick for them.
    const dismissTick = () => sfx.play('click');
    this.helpOverlay = new Overlay('helpOverlay', 'helpClose', dismissTick);
    this.studyOverlay = new Overlay('studyOverlay', 'studyCancel', dismissTick);
    this.settingsOverlay = new Overlay('settingsOverlay', 'settingsClose', dismissTick);
    this.langOverlay = new Overlay('langOverlay', 'langClose', dismissTick);
    this.feedbackOverlay = new Overlay('feedbackOverlay', 'fbCancel', dismissTick);
    this.feedback = new FeedbackPanel(this.feedbackOverlay, () => this.feedbackContext());
    closeOnEscape([
      // The picker and the drop box open over settings and are the only
      // overlays that stack, so they are listed first: Escape closes the top
      // one, and top is order. Escape never touches the views — a page is
      // not a dialog.
      this.langOverlay,
      this.feedbackOverlay,
      this.helpOverlay,
      this.studyOverlay,
      this.settingsOverlay,
    ]);
    this.bindSettings();

    // The nav bar. One history entry may sit under an open view, so the
    // platform back gesture returns toward the board instead of exiting;
    // the failure mode of all of this is exactly the old behavior.
    history.replaceState({ view: 'today' }, '');
    window.addEventListener('popstate', (e) => {
      if (this.suppressPop) {
        this.suppressPop = false;
        return;
      }
      const v = (e.state as { view?: string } | null)?.view ?? 'today';
      this.showView(v in VIEW_IDS ? (v as AppView) : 'today', true);
    });
    must('navToday').addEventListener('click', () => {
      const onToday = this.mode === 'daily' && this.playDay === this.today;
      this.toBoard();
      // Never re-deals a board it is already showing: a scratch layout
      // mid-tune survives a stray Today tap.
      if (!onToday) this.startDaily(this.today);
    });
    must('navArchive').addEventListener('click', () => this.openSection('archive'));
    must('navDrills').addEventListener('click', () => this.openSection('drills'));
    must('navProfile').addEventListener('click', () => this.openSection('profile'));

    // Profile rows: doors to the surfaces that live nowhere else.
    must('profileHelp').addEventListener('click', () => {
      track('panel_view', { panel: 'help' });
      this.helpOverlay.open();
    });
    must('profileFeedback').addEventListener('click', () => {
      track('panel_view', { panel: 'feedback' });
      this.feedback.open();
    });
    must('profileSettings').addEventListener('click', () => {
      track('panel_view', { panel: 'settings' });
      this.settingsOverlay.open();
    });

    must('helpBtn').addEventListener('click', () => {
      track('panel_view', { panel: 'help' });
      this.helpOverlay.open();
    });
    // Practice is picked from the Drills view, so the pick returns to the
    // board screen the way a drill row does.
    must('practiceBtn').addEventListener('click', () => {
      this.toBoard();
      this.startPractice();
    });
    must('newMapBtn').addEventListener('click', () => this.startPractice());

    // The rules overlay keeps its secondary door to the drills (STYLEGUIDE
    // §7): a player who wants more of them is already reading them.
    must('yardOpenBtn').addEventListener('click', () => {
      this.helpOverlay.close();
      this.openSection('drills');
    });

    must('nextDrillBtn').addEventListener('click', () => {
      const idx = DRILLS.findIndex((d) => d.id === this.drillId);
      const next = idx >= 0 ? DRILLS[idx + 1] : undefined;
      if (next) this.startDrill(next.id);
      else this.startDaily(this.today);
    });

    must('yesterdayBtn').addEventListener('click', () => this.startDaily(this.today - 1));
    must('revealBtn').addEventListener('click', () => this.toggleReveal());
    must('studyConfirm').addEventListener('click', () => {
      this.studyOverlay.close();
      this.studyWarned = true;
      void markStudyWarningSeen();
      this.showReference();
    });

    must('shareBtn').addEventListener('click', () => void this.share());
    this.paintShareVerb();
  }

  /**
   * The primary names what it will do: hand the result to the platform's
   * share sheet, or copy it where no sheet exists. The verb on the button is
   * the verb in the toast (STYLEGUIDE §10). Restated after every language
   * change, because the static pass writes the copy verb over it.
   */
  private paintShareVerb(): void {
    if (canShareSheet()) must('shareBtn').textContent = str().shareResult;
  }

  /**
   * The settings row's own value: the language now being spoken, written in
   * that language. Never translated, so the row stays readable to the player
   * who most needs it — someone looking at an interface they cannot read.
   */
  private paintLangRow(): void {
    must('langCurrent').textContent = LOCALE_NAME[locale()];
  }

  /**
   * The picker: every language the game speaks, each in its own name, with
   * the one in use marked the way the yard marks the drill on the board.
   */
  private paintLangList(): void {
    const list = must('langList');
    const here = locale();
    list.innerHTML = LOCALES.map(
      (l) =>
        `<button class="arch-row${l === here ? ' current' : ''}" data-locale="${l}"` +
        `${l === here ? ' aria-current="true"' : ''}>` +
        `<span class="drill-name"><b>${LOCALE_NAME[l]}</b></span></button>`,
    ).join('');
    list.querySelectorAll<HTMLButtonElement>('.arch-row').forEach((b) => {
      b.addEventListener('click', () => {
        const picked = b.dataset.locale;
        if (!isLocale(picked)) return;
        this.langOverlay.close();
        if (picked === locale()) return;
        // settingOn says which way a breaker was thrown; a picker has no off,
        // so the fact worth filing is that the language was changed at all.
        track('set_change', { setting: 'language', settingOn: true });
        this.relanguage(picked);
      });
    });
  }

  /**
   * Speak a different language from here on.
   *
   * Everything already on screen is restated rather than rebuilt: the board
   * carries no words, so the grid, the towers and the run in progress are
   * untouched, and a player switching language mid-tune loses nothing.
   */
  private relanguage(next: Locale): void {
    setLocale(next);
    void saveLocale(next);
    applyStaticStrings();
    this.paintLangRow();
    this.paintShareVerb();
    // A toast outlives the switch: the offline notice lands during boot and
    // is often still up when the player picks a language.
    restateToast();
    this.paintDaytag();
    // Carries the drill lesson and the reveal button's verb with it.
    this.setButtons();
    if (this.session.solvedOnce) this.showPanel(this.session.evaluate().mw);
    this.tickNextGrid();
    // The lists are built on entry, so only the view on screen needs redoing.
    if (this.activeView !== 'today') this.showView(this.activeView);
    // The panel owns its own chips, placeholders and countdown, so the static
    // pass cannot reach them.
    if (this.feedbackOverlay.isOpen) this.feedback.paint();
  }

  /**
   * The settings switches, plus the gesture that lets audio start at all —
   * browsers refuse to open an audio context before the player touches the
   * page. The unlock stays armed for the whole session: after an iOS
   * interruption (backgrounding, a call, Siri) the context sometimes only
   * resumes from inside a gesture, and re-checking is free once it's running.
   */
  /** The day list, built fresh from saved progress. A pick returns to the
   *  board screen with that grid loaded — the Archive tab stays lit for it,
   *  because the lit tab is provenance (STYLEGUIDE §7). */
  private paintArchive(): void {
    renderArchive(
      buildArchiveRows(this.progress, this.today, this.mode === 'daily' ? this.playDay : null),
      (day) => {
        this.toBoard();
        this.startDaily(day);
        must('board').focus({ preventScroll: true });
      },
    );
  }

  /** The drill list, in authored order, marking the one on the board. */
  private paintYard(): void {
    renderYard(buildYardRows(DRILLS, this.drills, this.drillId), (id) => {
      this.toBoard();
      this.startDrill(id);
      must('board').focus({ preventScroll: true });
    });
  }

  private bindSettings(): void {
    document.addEventListener('pointerdown', () => sfx.unlock());

    // Park audio while hidden and nudge it on return. Three events for one
    // moment: pageshow covers Safari's back/forward cache and focus covers
    // the iOS app switches where neither of the others fires — no single
    // event is reliable across every way back to the page.
    const onLifecycle = () => {
      if (document.hidden) sfx.suspend();
      else sfx.wake();
    };
    document.addEventListener('visibilitychange', onLifecycle);
    window.addEventListener('pageshow', onLifecycle);
    window.addEventListener('focus', onLifecycle);

    // Close the grid's measurement window from inside the dying document, or
    // the last grid of every session reads as a silent walk-out. iOS reaps a
    // tab without firing pagehide, so hidden has to count too — a grid the
    // player comes back to simply opens a new window on the next deal.
    window.addEventListener('pagehide', () => this.closeMeasure({ keep: true }));
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.closeMeasure({ keep: true });
      else if (this.measuring) this.measuring.reported = false;
    });

    must('settingsBtn').addEventListener('click', () => {
      track('panel_view', { panel: 'settings' });
      this.settingsOverlay.open();
    });

    // The one row in the list that opens something instead of throwing.
    must('setLanguage').addEventListener('click', () => {
      track('panel_view', { panel: 'language' });
      this.paintLangList();
      this.langOverlay.open();
    });
    this.paintLangRow();

    bindSwitch('setSound', !sfx.muted, (on) => {
      sfx.setMuted(!on);
      void saveMuted(!on);
      track('set_change', { setting: 'sound', settingOn: on });
      // Confirm the change audibly — but only when turning it back on.
      sfx.play('click');
    });

    // The haptics breaker only exists where the API does; iOS never sees it.
    setVisible(must('setHaptics'), hapticsSupported());
    bindSwitch('setHaptics', hapticsOn(), (on) => {
      setHaptics(on);
      void saveHaptics(on);
      track('set_change', { setting: 'haptics', settingOn: on });
      sfx.play(on ? 'click' : 'clickOff');
      // Confirm in the channel itself, like the sound switch does.
      if (on) buzz(BUZZ.build);
    });

    const paintMotion = bindSwitch('setMotion', prefersReducedMotion(), (reduced) => {
      setReducedMotionOverride(reduced);
      void saveMotionOverride(reduced);
      track('set_change', { setting: 'motion', settingOn: reduced });
      this.board.refreshMotion();
      sfx.play(reduced ? 'click' : 'clickOff');
    });
    // The OS preference can flip mid-session; while no override is stored the
    // effective state follows it, so the flag and the switch track along.
    globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').addEventListener('change', () => {
      syncReducedMotionAttr();
      this.board.refreshMotion();
      paintMotion(prefersReducedMotion());
    });

    const paintContrast = bindSwitch('setContrast', prefersContrast(), (on) => {
      setContrastOverride(on);
      void saveContrast(on);
      track('set_change', { setting: 'contrast', settingOn: on });
      sfx.play(on ? 'click' : 'clickOff');
    });
    // The OS preference can flip mid-session; while no override is stored the
    // effective state follows it, so the flag and the switch track along.
    globalThis.matchMedia?.('(prefers-contrast: more)').addEventListener('change', () => {
      syncContrastAttr();
      paintContrast(prefersContrast());
    });

    bindSwitch('setText', flagApplied('data-large-text'), (on) => {
      applyFlag('data-large-text', on);
      void saveLargeText(on);
      track('set_change', { setting: 'text', settingOn: on });
      sfx.play(on ? 'click' : 'clickOff');
    });

    bindSwitch('setUsage', this.usage, (on) => {
      this.usage = on;
      void saveUsage(on);
      if (on) {
        // Arm first, or the flip that turns the stream back on is the one
        // event the stream is still too switched-off to carry.
        setTelemetryEnabled(true);
        track('set_change', { setting: 'usage', settingOn: true });
        this.loadField(this.playDay);
      } else {
        // Record before stopping: the flip describing itself is the last
        // thing that ever leaves (GDD §12).
        track('set_change', { setting: 'usage', settingOn: false });
        setTelemetryEnabled(false);
        // The readout goes with the stream, in both directions (GDD §4.7).
        this.field = null;
        this.yesterdayView = null;
        if (this.session.solvedOnce) this.showPanel(this.session.evaluate().mw);
      }
      sfx.play(on ? 'click' : 'clickOff');
    });

    // Hidden rather than dead where no drop box is configured: a form whose
    // every send fails is worse than no form (GDD §13).
    setVisible(must('feedbackBtn'), feedbackEnabled());
    must('feedbackBtn').addEventListener('click', () => {
      track('panel_view', { panel: 'feedback' });
      this.feedback.open();
    });

    // A new tab rather than a navigation: an installed copy that walks off to
    // a document has no way back to the grid.
    must('privacyBtn').addEventListener('click', () => {
      window.open('./privacy.html', '_blank', 'noopener');
    });

    // One soft tick for anything else clickable. Board taps have their own
    // sounds, the share button has its own confirmation, and the settings
    // breakers speak for themselves — on and off are different throws.
    document.addEventListener('click', (e) => {
      const target = (e.target as Element | null)?.closest('button');
      if (!target || target.id === 'shareBtn' || target.classList.contains('setting')) return;
      sfx.play('click');
    });
  }

  /** Restore persisted settings before anything can make a noise or animate. */
  private async loadSettings(): Promise<void> {
    sfx.setMuted(await loadMuted());
    setHaptics(await loadHaptics());
    // `null` follows the OS — the sync inside still stamps the effective flag.
    setReducedMotionOverride(await loadMotionOverride());
    // `null` follows the OS — the sync inside still stamps the effective flag.
    setContrastOverride(await loadContrast());
    applyFlag('data-large-text', await loadLargeText());
    this.usage = await loadUsage();
  }

  private async share(): Promise<void> {
    const best = this.session.best;
    const tier = this.session.tier();
    if (!best || !tier || this.mode === 'drill') return;

    const matched = this.session.matchedReference;
    const text = buildShareText({
      mode: this.mode,
      day: this.playDay,
      isToday: this.playDay === this.today,
      best,
      firstMW: this.session.firstMW,
      par: this.session.puzzle.par,
      tier,
      beatReference: this.session.beatReference,
      matched,
      archetype: this.session.puzzle.archetype,
      trail: this.session.mwTrail,
      // The name describes the reference, so it stays out of the share until
      // a perfect proves the player already knows it (GDD §6.3).
      topology: matched ? this.topologyName() : null,
      flawless: this.session.flawless,
      studied: this.session.studied,
      streak: this.progress.streak,
    });

    const outcome = await shareResult(text);
    const landed = outcome === 'shared' || outcome === 'copied';
    // Whether the handoff landed, never what was in it.
    track('share', { ...this.gridKeys(), shareOk: landed });
    // A dismissed sheet is a change of mind: no cue, no toast, nothing owed.
    if (outcome === 'dismissed') return;
    // A blocked handoff is a chrome error, not board physics — never the hum.
    sfx.play(landed ? 'copy' : 'copyFail');
    // The sheet is its own confirmation; only the quiet clipboard gets one.
    if (outcome === 'shared') return;
    toast(() => (outcome === 'copied' ? str().resultCopied : str().copyBlocked));
  }
}
